import { useState, useMemo, useCallback, useEffect, type CSSProperties } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import AOS from "aos";

export type KbLeaf = { id: string; label: string };
export type KbBranch = {
  id: string;
  number?: string;
  label: string;
  children: KbNode[];
};
export type KbNode = KbLeaf | KbBranch;

export type KnowledgeBaseMindMapProps = {
  rootLabel: string;
  rootColor: string;
  /** i18n key for `t(key, { returnObjects: true })` returning `KbBranch[]` */
  treeTranslationKey: string;
  /** e.g. `furniture.knowledgeBase` — used for eyebrow, title, subtitle, legend strings */
  i18nPrefix: string;
  leafHref?: string;
};

const BRANCH_COLORS = ["#0ea5e9", "#10b981", "#f59e0b"];

const isKbBranch = (node: KbNode): node is KbBranch =>
  Array.isArray((node as KbBranch).children);

function toTreeNode(node: KbNode, level: number, index = 0): any {
  if (!isKbBranch(node)) {
    return { id: node.id, label: node.label?.length > 50 ? node.label.slice(0, 50) + "..." : node.label, isLeaf: true };
  }

  const prefix = level === 1 && node.number ? `${node.number} ` : "";
  const branchColor = level === 1 ? BRANCH_COLORS[index] || BRANCH_COLORS[0] : undefined;

  return {
    id: node.id,
    label: `${prefix}${node.label}`,
    ...(branchColor ? { bc: branchColor } : {}),
    children: node.children.map((child, childIndex) => toTreeNode(child, level + 1, childIndex)),
  };
}

const NH = [52, 40, 36, 32];
const NFS = [16, 13, 12, 11];
const HGAP = [80, 60, 50];
const VGAP = 16;
const PAD = 60;

function hex2rgba(hex: string, a: number) {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${a})`;
}

function estW(label: string, lv: number) {
  const fs = NFS[lv] || 11;
  const cjk = (label.match(/[\u4e00-\u9fff\u3000-\u303f\uff00-\uffef]/g) || []).length;
  const rest = label.length - cjk;
  const px = [28, 16, 14, 12][lv] || 12;
  const textW = Math.ceil(cjk * fs * 1.1 + rest * fs * 0.56);
  const minW = lv >= 2 ? Math.max(90, fs * 10) : 0;
  // return minW;
  return Math.max(minW, textW + px * 2 + 16);
}

function layout(root: any) {
  const t = JSON.parse(JSON.stringify(root));
  const rootBc = t.bc || "#6366f1";
  function calcSH(n: any, lv: number) {
    const h = NH[lv] || 32;
    if (!n.children?.length) {
      n._sh = h;
      return h;
    }
    let s = 0;
    n.children.forEach((c: any, i: number) => {
      s += calcSH(c, lv + 1);
      if (i < n.children.length - 1) s += VGAP;
    });
    n._sh = Math.max(h, s);
    return n._sh;
  }

  const nodes: any[] = [],
    conns: any[] = [];
  function assign(n: any, lv: number, x: number, yc: number, bid: string, bc: string) {
    const h = NH[lv] || 32;
    const w = estW(n.label, lv);
    const color = lv <= 1 ? n.bc || bc : bc;
    const branch = lv === 0 ? "root" : lv === 1 ? n.id : bid;

    nodes.push({ ...n, x, y: yc, w, h, lv, branch, color });

    if (!n.children?.length) return;
    const hg = HGAP[lv] || 50;
    const cx = x + w + hg;
    let tch = 0;
    n.children.forEach((c: any, i: number) => {
      tch += c._sh;
      if (i < n.children.length - 1) tch += VGAP;
    });
    let cy = yc - tch / 2;

    n.children.forEach((child: any) => {
      const cc = cy + child._sh / 2;
      const dx = cx - (x + w);
      conns.push({
        id: `${n.id}->${child.id}`,
        d: `M${x + w},${yc} C${x + w + dx * 0.5},${yc} ${cx - dx * 0.5},${cc} ${cx},${cc}`,
        color: lv === 0 ? child.bc : color,
        branch: lv === 0 ? child.id : branch,
        lv,
      });
      assign(child, lv + 1, cx, cc, lv === 0 ? child.id : branch, lv === 0 ? child.bc : color);
      cy += child._sh + VGAP;
    });
  }

  calcSH(t, 0);
  assign(t, 0, PAD, t._sh / 2 + PAD, "root", rootBc);
  let mr = 0;
  nodes.forEach((n) => {
    if (n.x + n.w > mr) mr = n.x + n.w;
  });
  return { nodes, conns, W: mr + PAD, Ht: t._sh + PAD * 2 };
}

function MapNode({
  data,
  hovered,
  onHover,
  onLeave,
  leafHref,
  mapIndex,
}: {
  data: any;
  hovered: string | null;
  onHover: (id: string) => void;
  onLeave: () => void;
  leafHref: string;
  mapIndex: number;
}) {
  const navigate = useNavigate();
  const { id, label, x, y, w, h, lv, color, isLeaf } = data;
  const hi = hovered === id;
  const dim = hovered && hovered !== id;

  const handleClick = () => {
    if (isLeaf) navigate(leafHref);
  };

  const style: CSSProperties = {
    position: "absolute",
    left: x,
    top: y - h / 2,
    width: w,
    height: h,
    display: "flex",
    alignItems: "center",
    justifyContent: lv === 0 ? "center" : "flex-start",
    padding: `0 ${[24, 16, 14, 12][lv]}px`,
    borderRadius: [16, 12, 8, 6][lv],
    fontSize: NFS[lv],
    fontWeight: lv <= 1 ? 600 : 500,
    cursor: isLeaf ? "pointer" : "default",
    transition: "all 0.3s cubic-bezier(0.23, 1, 0.32, 1)",
    zIndex: hi ? 20 : 10,
    opacity: dim ? 0.9 : 1,
    transform: hi ? "translateX(2px) scale(0.99)" : "scale(1)",
  };

  let visualStyle: CSSProperties = {};
  if (lv === 0) {
    visualStyle = {
      background: `linear-gradient(135deg, ${color}, ${hex2rgba(color, 0.8)})`,
      color: "#fff",
      boxShadow: `0 10px 25px -5px ${hex2rgba(color, 0.4)}`,
    };
  } else if (lv === 1) {
    visualStyle = {
      background: hi ? hex2rgba(color, 0.08) : "#fff",
      color: hi ? color : "#1e293b",
      border: `1.5px solid ${hi ? color : "#e2e8f0"}`,
      boxShadow: hi ? `0 8px 20px -6px ${hex2rgba(color, 0.25)}` : "0 2px 4px rgba(0,0,0,0.02)",
    };
  } else {
    visualStyle = {
      background: hi ? hex2rgba(color, 0.08) : "transparent",
      color: hi ? color : "#64748b",
      border: `1.5px solid ${hi ? color : "#e2e8f0"}`,
      borderLeft: `3px solid ${hi ? color : "#cbd5e1"}`,
    };
  }

  return (
    <div
      style={{ ...style, ...visualStyle }}
      onMouseEnter={() => onHover(id)}
      onMouseLeave={onLeave}
      onClick={handleClick}
      className="group"
      data-aos="fade-up"
      data-aos-delay={Math.min(mapIndex * 28, 480)}
      data-aos-duration="550"
      data-aos-easing="ease-out-cubic"
    >
      <span className="truncate">{label}</span>
      {isLeaf && (
        <ArrowUpRight className="size-3 shrink-0 translate-x-0.5 opacity-0 transition-all duration-300 group-hover:translate-x-1.5 group-hover:opacity-100 absolute right-3.5 top-1/2 -translate-y-1/2" />
      )}
    </div>
  );
}

export default function KnowledgeBaseMindMap({
  rootLabel,
  rootColor,
  treeTranslationKey,
  i18nPrefix,
  leafHref = "/schemeCustomization",
}: KnowledgeBaseMindMapProps) {
  const { t } = useTranslation();
  const [hovered, setHovered] = useState<string | null>(null);
  const tree = useMemo(
    () => ({
      id: "root",
      label: rootLabel,
      bc: rootColor,
      children: (t(treeTranslationKey, { returnObjects: true }) as KbBranch[]).map((branch, index) =>
        toTreeNode(branch, 1, index)
      ),
    }),
    [t, rootLabel, rootColor, treeTranslationKey]
  );
  const L = useMemo(() => layout(tree), [tree]);

  useEffect(() => {
    AOS.init({
      offset: 80,
      easing: "ease-out-cubic",
      duration: 750,
      once: true,
    });
  }, []);

  useEffect(() => {
    AOS.refresh();
  }, [tree, L.W, L.Ht]);

  const handleHover = useCallback((b: string) => setHovered(b), []);
  const handleLeave = useCallback(() => setHovered(null), []);

  return (
    <div className="relative flex min-h-screen w-full flex-col items-center overflow-auto bg-[#fcfcfd] px-8 py-16 antialiased">
      <div className="pointer-events-none fixed inset-0">
        <div className="absolute top-[-10%] left-[-10%] h-[40%] w-[40%] rounded-full bg-blue-50/50 blur-[120px]" />
        <div className="absolute right-[-10%] bottom-[-10%] h-[40%] w-[40%] rounded-full bg-indigo-50/50 blur-[120px]" />
      </div>

      <div className="relative z-10 text-center">
        <div
          className="mb-4 inline-block rounded-full border border-indigo-100 bg-indigo-50 px-3 py-1 text-[10px] font-bold tracking-widest text-indigo-600 uppercase"
          data-aos="fade-down"
          data-aos-duration="600"
        >
          {t(`${i18nPrefix}.eyebrow`)}
        </div>
        <h1
          className="text-4xl font-extrabold tracking-tight text-slate-900 sm:text-5xl"
          data-aos="fade-up"
          data-aos-delay="80"
          data-aos-duration="700"
        >
          {t(`${i18nPrefix}.title`)}
        </h1>
        <p
          className="mt-6 font-medium text-slate-500 max-w-3xl mx-auto leading-relaxed"
          data-aos="fade-up"
          data-aos-delay="160"
          data-aos-duration="700"
        >
          {t(`${i18nPrefix}.subtitle`)}
        </p>
      </div>

      <div
        style={{ width: L.W, height: L.Ht, position: "relative" }}
        className="z-10"
        data-aos="zoom-in"
        data-aos-delay="100"
        data-aos-duration="800"
      >
        <svg width={L.W} height={L.Ht} className="pointer-events-none absolute inset-0">
          {L.conns.map((c) => {
            const [fromId, toId] = c.id.split("->");
            const hi = hovered === fromId || hovered === toId;
            const dim = hovered && !hi;
            return (
              <path
                key={c.id}
                d={c.d}
                fill="none"
                stroke={hi ? c.color : "#94a3b8"}
                strokeWidth={hi ? 3 : 2}
                opacity={dim ? 0.6 : hi ? 1 : 0.8}
                strokeLinecap="round"
                style={{ transition: "all 0.3s ease" }}
              />
            );
          })}
        </svg>

        {L.nodes.map((n: any, mapIndex: number) => (
          <MapNode
            key={n.id}
            data={n}
            hovered={hovered}
            onHover={handleHover}
            onLeave={handleLeave}
            leafHref={leafHref}
            mapIndex={mapIndex}
          />
        ))}
      </div>

      <div
        className="flex gap-8 rounded-2xl border border-slate-100 bg-white/50 px-8 py-4 text-xs font-medium tracking-wide text-slate-400 backdrop-blur-sm"
        data-aos="fade-up"
        data-aos-delay="120"
        data-aos-duration="650"
      >
        <div className="flex items-center gap-2">
          <span className="h-2 w-2 rounded-full bg-indigo-500" /> {t(`${i18nPrefix}.legendMainTopics`)}
        </div>
        <div className="flex items-center gap-2">
          <span className="h-2 w-2 rounded-full bg-slate-300" /> {t(`${i18nPrefix}.leafLabel`)}
        </div>
        <div className="flex items-center gap-2">
          <span className="h-2 w-2 rounded-full border border-indigo-400" /> {t(`${i18nPrefix}.jumpHint`)}
        </div>
      </div>
    </div>
  );
}
